import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { v4 as uuidv4 } from 'uuid';
import { LoginTrackingApiService } from './login-tracking-api.service';
import { StorageService } from './storage.service';
import { AppConfig } from '../../app.config';

export const LOGIN_STAGE = {
  LOGIN_INIT: 'LOGIN_INIT',
  SIGNALR_INIT: 'SIGNALR_INIT',
  BROWSER_OPENED: 'BROWSER_OPENED',
  AUTH_CODE_RECEIVED: 'AUTH_CODE_RECEIVED',
  WEB_TOKEN_SUCCESS: 'WEB_TOKEN_SUCCESS',
  WEB_TOKEN_FAILED: 'WEB_TOKEN_FAILED',
  TOKEN_EXCHANGE_INIT: 'TOKEN_EXCHANGE_INIT',
  TOKEN_EXCHANGE_SUCCESS: 'TOKEN_EXCHANGE_SUCCESS',
  TOKEN_EXCHANGE_FAILED: 'TOKEN_EXCHANGE_FAILED',
  PROFILE_FETCH_FAILED: 'PROFILE_FETCH_FAILED',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILED: 'LOGIN_FAILED',
} as const;

@Injectable({
  providedIn: 'root',
})
export class LoginTrackingService {
  private sessionId: string = '';
  private readonly platform: string = Capacitor.getPlatform();
  private appVersion: string = '';
  private deviceId: string = '';
  private osVersion: string = '';
  private model: string = '';
  private manufacturer: string = '';

  constructor(
    private readonly loginTrackingApiService: LoginTrackingApiService,
    private readonly storageService: StorageService
  ) {
    this.initDeviceInfo();
  }

  private async initDeviceInfo() {
    if (Capacitor.isNativePlatform()) {
      try {
        const [appInfo, deviceInfo] = await Promise.all([
          App.getInfo(),
          Device.getInfo(),
        ]);
        this.appVersion = appInfo.version;
        this.osVersion = deviceInfo.osVersion || '';
        this.model = deviceInfo.model || '';
        this.manufacturer = deviceInfo.manufacturer || '';
      } catch {
        // ignore — not critical
      }
    }

    // Persist a stable device ID across sessions
    const stored = this.storageService.getDataFromStorage('css_device_id');
    if (stored) {
      this.deviceId = stored;
    } else {
      this.deviceId = uuidv4();
      this.storageService.putDataInStorage('css_device_id', this.deviceId);
    }
  }

  startNewSession(): string {
    this.sessionId = uuidv4();
    return this.sessionId;
  }

  getSessionId(): string {
    return this.sessionId;
  }

  getDeviceId(): string {
    return this.deviceId;
  }

  track(stage: string, message?: string) {
    if (!AppConfig.settings?.LOGIN_TRACKING_ENABLED) {
      return;
    }

    if (!this.sessionId) {
      this.startNewSession();
    }

    const payload = {
      session_id: this.sessionId,
      stage,
      platform: this.platform,
      app_version: this.appVersion || null,
      device_id: this.deviceId || null,
      os_version: this.osVersion || null,
      model: this.model || null,
      manufacturer: this.manufacturer || null,
      message: message || null,
    };

    this.loginTrackingApiService.trackLoginStep(payload).subscribe({
      error: (err) =>
        console.warn(`LoginTracking: failed to track stage [${stage}]`, err),
    });
  }
}
